/** HTTP API for Ethernet on the robot (FastAPI + nmcli), default port 12398. */
import { joinUrl } from "lib/utils/connection";
import { isHttpSecure } from "lib/utils/utils";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";
import { throwUserFacingHttpError } from "lib/utils/userFacingApiError";

export type EthernetStatusResponse = {
    interface: string;
    state?: string | null;
    connection?: string | null;
    autoconnect?: boolean | null;
    ipv4?: string | null;
    ipv4_gateway?: string | null;
    mac?: string | null;
    error?: string | null;
};

export type EthernetConnectRequest = {
    ipv4_method: "auto" | "manual";
    ipv4_address?: string | null;
    ipv4_gateway?: string | null;
    autoconnect: boolean;
};

export type EthernetAutoconnectRequest = {
    enabled: boolean;
};

export type EthernetActionResponse = {
    ok: boolean;
    message: string;
};

export type EthernetSavedProfile = {
    id: string;
    created_at: string;
    display_name?: string | null;
    ipv4_method: string;
    ipv4_address?: string | null;
    ipv4_gateway?: string | null;
    autoconnect: boolean;
    nm_connection?: string | null;
};

const DEFAULT_ETHERNET_API_PORT = "12398";


/** Base URL with no trailing slash. Uses the same host resolution pattern as Wi-Fi unless overridden. */
export function getEthernetApiBaseUrl(selectedArcHost?: string | null, isSecure: boolean = isHttpSecure(), arcId?: string | null): string | null {
    if (process.env.REACT_APP_ETHERNET_API_DISABLED === "true") {
        return null;
    }
    const explicit = process.env.REACT_APP_ETHERNET_API_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port = process.env.REACT_APP_ETHERNET_API_PORT?.trim() || DEFAULT_ETHERNET_API_PORT;
    const proxyHost =
        selectedArcHost?.trim() ||
        process.env.REACT_APP_ETHERNET_API_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    // Direct mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    const gatewayBase = gatewayServiceBaseUrl("ethernet", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

export async function fetchEthernetStatus(baseUrl: string): Promise<EthernetStatusResponse> {
    const url = joinUrl(baseUrl, "/ethernet/status");
    const res = await authedFetch(url);
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't load Ethernet status. Try again.");
    return res.json() as Promise<EthernetStatusResponse>;
}

export async function postEthernetConnect(
    baseUrl: string,
    body: EthernetConnectRequest
): Promise<EthernetActionResponse> {
    const url = joinUrl(baseUrl, "/ethernet/connect");
    const res = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ipv4_method: body.ipv4_method,
            ipv4_address: body.ipv4_method === "manual" ? (body.ipv4_address?.trim() || null) : null,
            ipv4_gateway: body.ipv4_method === "manual" ? (body.ipv4_gateway?.trim() || null) : null,
            autoconnect: body.autoconnect,
        }),
    });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't connect Ethernet. Try again.");
    return res.json() as Promise<EthernetActionResponse>;
}

export async function postEthernetDisconnect(baseUrl: string): Promise<EthernetActionResponse> {
    const url = joinUrl(baseUrl, "/ethernet/disconnect");
    const res = await authedFetch(url, { method: "POST" });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't disconnect Ethernet. Try again.");
    return res.json() as Promise<EthernetActionResponse>;
}

export async function fetchEthernetProfiles(baseUrl: string): Promise<EthernetSavedProfile[]> {
    const url = joinUrl(baseUrl, "/ethernet/profiles");
    const res = await authedFetch(url);
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't load Ethernet profiles. Try again.");
    return res.json() as Promise<EthernetSavedProfile[]>;
}

export type EthernetProfilePatchRequest = {
    display_name?: string | null;
    ipv4_method?: "auto" | "manual";
    ipv4_address?: string | null;
    ipv4_gateway?: string | null;
    autoconnect?: boolean;
};

export async function patchEthernetProfile(
    baseUrl: string,
    profileId: string,
    body: EthernetProfilePatchRequest
): Promise<EthernetSavedProfile> {
    const url = joinUrl(baseUrl, `/ethernet/profiles/${encodeURIComponent(profileId)}`);
    const res = await authedFetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't update the Ethernet profile. Try again.");
    return res.json() as Promise<EthernetSavedProfile>;
}

export async function deleteEthernetProfile(
    baseUrl: string,
    profileId: string
): Promise<{ ok: boolean }> {
    const url = joinUrl(baseUrl, `/ethernet/profiles/${encodeURIComponent(profileId)}`);
    const res = await authedFetch(url, { method: "DELETE" });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't remove the Ethernet profile. Try again.");
    return res.json() as Promise<{ ok: boolean }>;
}

export async function postEthernetConnectProfile(
    baseUrl: string,
    profileId: string
): Promise<EthernetActionResponse> {
    const url = joinUrl(baseUrl, `/ethernet/profiles/${encodeURIComponent(profileId)}/connect`);
    const res = await authedFetch(url, { method: "POST" });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't apply the Ethernet profile. Try again.");
    return res.json() as Promise<EthernetActionResponse>;
}

export async function postEthernetAutoconnect(
    baseUrl: string,
    body: EthernetAutoconnectRequest
): Promise<EthernetActionResponse> {
    const url = joinUrl(baseUrl, "/ethernet/autoconnect");
    const res = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: body.enabled }),
    });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't update Ethernet auto-connect. Try again.");
    return res.json() as Promise<EthernetActionResponse>;
}
